import ConversationListItem from './ConversationListItem';
import { getConnectionBadgeLabel } from './connectionBadge';
import { MdMessage } from 'react-icons/md';

function getOtherUser(conv, currentUserId) {
  return conv.otherUser || conv.participants?.find((p) => p.id !== currentUserId);
}

export default function ConversationList({
  conversations,
  friendsByUserId,
  currentUserId,
  selectedConversationId,
  pinnedIds,
  archivedIds,
  mutedIds,
  onSelectConversation,
  onPin,
  onArchive,
  onMute,
  onDelete,
  onMarkRead,
  onNewMessage,
  loading,
  error,
  onRetry,
  emptyMessage = 'Nema razgovora',
  searchEmptyMessage = 'Nema rezultata',
  hasSearchQuery,
  t = (s) => s,
}) {
  const getConnectionLabel = (conv) => {
    const other = getOtherUser(conv, currentUserId);
    const friend = other && friendsByUserId ? friendsByUserId[other.id] : null;
    return getConnectionBadgeLabel(friend?.relationshipType);
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
            <div className="w-11 h-11 rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-700" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 focus:ring-2 focus:ring-primary-500"
        >
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
          <MdMessage className="text-2xl text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {hasSearchQuery ? searchEmptyMessage : emptyMessage}
        </p>
        <button
          type="button"
          onClick={onNewMessage}
          className="px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 focus:ring-2 focus:ring-primary-500"
        >
          Započni novu poruku
        </button>
      </div>
    );
  }

  const withFriends = conversations.filter((c) => {
    const other = getOtherUser(c, currentUserId);
    return other && friendsByUserId && friendsByUserId[other.id];
  });
  const withOthers = conversations.filter((c) => {
    const other = getOtherUser(c, currentUserId);
    return !other || !friendsByUserId || !friendsByUserId[other.id];
  });

  const renderConvList = (list) => (
    <ul className="p-3 space-y-2" role="list">
      {list.map((conv) => {
        const other = getOtherUser(conv, currentUserId);
        const name = other?.name || (other?.firstName && other?.lastName ? `${other.firstName} ${other.lastName}` : null) || other?.email || 'Korisnik';
        const photoUrl = other?.photoUrl || other?.photo_url || other?.profileImage;
        return (
          <li key={conv.id}>
            <ConversationListItem
              conversation={conv}
              otherUserDisplayName={name}
              otherUserPhotoUrl={photoUrl}
              connectionBadgeLabel={getConnectionLabel(conv)}
              isSelected={selectedConversationId === conv.id}
              isUnread={(conv.unreadCount ?? 0) > 0}
              isPinned={pinnedIds.has(conv.id)}
              isArchived={archivedIds.has(conv.id)}
              isMuted={mutedIds.has(conv.id)}
              onSelect={onSelectConversation}
              onPin={onPin}
              onArchive={onArchive}
              onMute={onMute}
              onDelete={onDelete}
              onMarkRead={onMarkRead}
              t={t}
            />
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="flex-1 overflow-y-auto">
      {withFriends.length > 0 && (
        <>
          <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Prijatelji
          </p>
          {renderConvList(withFriends)}
        </>
      )}
      {withOthers.length > 0 && (
        <>
          <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Nannity članovi
          </p>
          {renderConvList(withOthers)}
        </>
      )}
    </div>
  );
}
